import { useState, useEffect, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { 
  TrendingUp, TrendingDown, DollarSign, 
  PiggyBank, ArrowUpRight, ArrowDownRight,
  Calendar, Printer, Eye, EyeOff,
  Lightbulb, X,
  CheckCircle, AlertCircle, Info, Settings2, Loader2,
  Wallet, ArrowUp, ArrowDown, Activity
} from 'lucide-react'
import CategoryIcon from '../components/CategoryIcon'
import TourGuide, { TourStep } from '../components/TourGuide'
import MarketTicker from '../components/MarketTicker'
import WidgetConfigPanel from '../components/WidgetConfigPanel'
import FinancialIntelligencePanel from '../components/FinancialIntelligencePanel'
import { useDashboardWidgets } from '../hooks/useDashboardWidgets'
import { useFinancialIntelligence } from '../hooks/useFinancialIntelligence'
import { useWorkspace } from '../hooks/useWorkspace'
import { 
  PieChart, Pie, Cell, 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, ReferenceLine,
  ComposedChart
} from 'recharts'
import api from '../services/api'
import {
  buildReconciliationLookup,
  buildStatementRequest,
  createStatementKpi,
  type AppInvoice,
  type AppTransaction,
  type RetrievalSnippet,
} from '../services/financialIntelligence'
import { useTheme } from '../hooks/useTheme'
import { useLanguage } from '../hooks/useLanguage'

interface DashboardData {
  current_month: {
    year: number
    month: number
    total_income: number
    total_expenses: number
    fixed_costs: number
    variable_costs: number
    savings: number
    fixed_percentage: number
    variable_percentage: number
  }
  previous_month?: {
    total_expenses: number
  }
  trend: {
    expenses: number
    fixed_costs: number
    variable_costs: number
  }
  top_categories: Array<{
    category_id: number
    category_name: string
    icon: string
    color: string
    total: number
    percentage: number
    transaction_count: number
  }>
  insights: string[]
}

interface CashFlowItem {
  category_name: string
  amount: number
  transaction_count: number
}

interface CashFlowStatement {
  period_start: string
  period_end: string
  opening_balance: number
  closing_balance: number
  operating: {
    inflows: CashFlowItem[]
    outflows: CashFlowItem[]
    total_inflows: number
    total_outflows: number
    net: number
  }
  investing: {
    inflows: CashFlowItem[]
    outflows: CashFlowItem[]
    total_inflows: number
    total_outflows: number
    net: number
  }
  financing: {
    inflows: CashFlowItem[]
    outflows: CashFlowItem[]
    total_inflows: number
    total_outflows: number
    net: number
  }
  net_cash_flow: number
  cash_change_percent: number
}

interface CashFlowKPIs {
  operating_cash_flow: number
  free_cash_flow: number
  cash_burn_rate: number
  runway_months: number
}

interface CashFlowPeriod {
  period: string
  statement: CashFlowStatement
}

interface CashFlowReportResponse {
  periods: CashFlowPeriod[]
  summary: CashFlowStatement
  kpis: CashFlowKPIs
}

export default function Dashboard() {
  const { isDark } = useTheme()
  const { t, language } = useLanguage()
  const { currentWorkspace } = useWorkspace()
  const { dashboardId } = useParams<{ dashboardId?: string }>()
  
  // Widget configuration
  const {
    widgets,
    isConfiguring,
    setIsConfiguring,
    toggleWidget,
    moveWidget,
    resetWidgets,
    isWidgetVisible,
  } = useDashboardWidgets(dashboardId)
  
  // Chart axis colors based on theme
  const axisColor = isDark ? '#9ca3af' : '#6b7280'
  
  // Market data query for ticker
  const { data: marketData, isLoading: marketLoading } = useQuery({
    queryKey: ['markets'],
    queryFn: async () => {
      const response = await api.get('/markets')
      return response.data
    },
    retry: 1,
    refetchInterval: 5 * 60 * 1000,
    staleTime: 2 * 60 * 1000,
  })
  
  // Privacy mode
  const [showAmounts, setShowAmounts] = useState(() => {
    const saved = localStorage.getItem('showAmounts')
    return saved === 'true'
  })
  
  const toggleShowAmounts = () => {
    const newValue = !showAmounts
    setShowAmounts(newValue)
    localStorage.setItem('showAmounts', String(newValue))
  }

  // Active preset state for filter buttons - default to last_month
  const [activePreset, setActivePreset] = useState<string | null>('last_month')
  
  // Date range state - default to last month
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const end = new Date(now.getFullYear(), now.getMonth(), 0)
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    }
  })

  const { data, isLoading, isFetching: dashboardFetching, error } = useQuery<DashboardData>({
    queryKey: ['dashboard', dateRange],
    queryFn: async () => {
      const response = await api.get(`/reports/dashboard?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      return response.data
    },
    retry: 1,
  })

  const { data: timelineData } = useQuery<{ data: Array<{ month: string; label: string; income: number; expenses: number }> }>({
    queryKey: ['timeline', dateRange],
    queryFn: async () => {
      const response = await api.get(`/reports/timeline?start_date=${dateRange.start}&end_date=${dateRange.end}`)
      return response.data
    },
    retry: 1,
  })

  // Cash Flow query
  const { data: cashFlowData, isLoading: cashFlowLoading } = useQuery<CashFlowReportResponse>({
    queryKey: ['cash-flow', dateRange],
    queryFn: async () => {
      const response = await api.get(`/reports/cash-flow?from=${dateRange.start}&to=${dateRange.end}&group_by=month`)
      return response.data
    },
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  const { data: aiInvoicesData } = useQuery<{ invoices: AppInvoice[]; total: number }>({
    queryKey: ['dashboard-ai-invoices', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/invoices?pageSize=80')
      return response.data
    },
    enabled: isWidgetVisible('financial-intelligence') && !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const { data: aiTransactionsResponse } = useQuery<{
    data?: AppTransaction[]
    transactions?: AppTransaction[]
  }>({
    queryKey: ['dashboard-ai-transactions', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/transactions/?per_page=240')
      return response.data
    },
    enabled: isWidgetVisible('financial-intelligence') && !!currentWorkspace?.id,
    staleTime: 5 * 60 * 1000,
  })

  const timeline = useMemo(() => timelineData?.data || [], [timelineData?.data])
  const aiInvoices = useMemo(() => aiInvoicesData?.invoices ?? [], [aiInvoicesData?.invoices])
  const aiTransactions = useMemo(
    () => aiTransactionsResponse?.data || aiTransactionsResponse?.transactions || [],
    [aiTransactionsResponse?.data, aiTransactionsResponse?.transactions],
  )

  const aiPeriodLabel = useMemo(() => {
    const locale = language === 'it' ? 'it-IT' : 'en-US'
    const start = new Date(dateRange.start).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    const end = new Date(dateRange.end).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    return `${start} - ${end}`
  }, [dateRange.end, dateRange.start, language])

  const reconciliationLookup = useMemo(
    () => buildReconciliationLookup(aiTransactions, aiInvoices),
    [aiInvoices, aiTransactions],
  )

  const canonicalReconciliationPayload = useMemo(() => {
    if (!currentWorkspace?.id) return null
    return {
      ensure_backfill: true,
      transaction_limit: 80,
      invoice_limit: 40,
      auto_match_threshold: 0.92,
      suggest_threshold: 0.72,
      model_version: 'lightgbm-v0',
    }
  }, [currentWorkspace?.id])

  const canonicalCashflowPayload = useMemo(
    () => ({
      ensure_backfill: true,
      lookback_days: 180,
      horizons_days: [7, 30, 60, 90],
      use_runpod_tft: true,
      model_version: 'tft-v0',
    }),
    [],
  )

  const statementPayload = useMemo(() => {
    if (!data || !currentWorkspace?.name) return null

    const currentPeriod = data.current_month
    const previousPeriod = data.previous_month
    const dashboardTrend = data.trend

    const executiveKpis = [
      createStatementKpi('Entrate del periodo', currentPeriod.total_income),
      createStatementKpi(
        'Uscite del periodo',
        currentPeriod.total_expenses,
        previousPeriod?.total_expenses,
      ),
      createStatementKpi('Risparmio del periodo', currentPeriod.savings),
      createStatementKpi(
        'Flusso di cassa operativo',
        cashFlowData?.kpis.operating_cash_flow ?? currentPeriod.savings,
      ),
    ]

    const technicalKpis = [
      createStatementKpi('Costi fissi', currentPeriod.fixed_costs, undefined),
      createStatementKpi('Costi variabili', currentPeriod.variable_costs, undefined),
      createStatementKpi('Incidenza costi fissi', currentPeriod.fixed_percentage, undefined, '%'),
      createStatementKpi(
        'Autonomia di cassa',
        cashFlowData?.kpis.runway_months ?? 0,
        undefined,
        'mesi',
      ),
    ]

    const alertFlags = [
      ...(currentPeriod.savings < 0 ? ['cashflow del periodo negativo'] : []),
      ...(currentPeriod.fixed_percentage >= 65 ? ['peso dei costi fissi sopra il 65%'] : []),
      ...((cashFlowData?.kpis.runway_months ?? 99) < 6 ? ['runway inferiore a 6 mesi'] : []),
      ...(dashboardTrend.expenses > 10
        ? ['accelerazione delle uscite rispetto al mese precedente']
        : []),
    ]

    const retrievalContext: RetrievalSnippet[] = data.insights.slice(0, 4).map((insight, index) => ({
      snippet_id: `dashboard-insight-${index}`,
      source: 'dashboard-insights',
      content: insight,
      score: 0.8,
    }))

    return buildStatementRequest({
      companyName: currentWorkspace.name,
      periodLabel: aiPeriodLabel,
      executiveKpis,
      technicalKpis,
      alertFlags,
      retrievalContext,
    })
  }, [
    aiPeriodLabel,
    cashFlowData?.kpis.operating_cash_flow,
    cashFlowData?.kpis.runway_months,
    currentWorkspace?.name,
    data,
  ])

  const financialIntelligence = useFinancialIntelligence({
    enabled: isWidgetVisible('financial-intelligence'),
    reconciliation: {
      source: 'canonical',
      canonicalPayload: canonicalReconciliationPayload,
      lookup: reconciliationLookup,
    },
    cashflow: {
      source: 'canonical',
      canonicalPayload: canonicalCashflowPayload,
    },
    statement: {
      payload: statementPayload,
    },
  })

  // ── Toast notification system for insights ──
  const [toasts, setToasts] = useState<Array<{ id: number; text: string; type: 'warning' | 'success' | 'info'; closing: boolean }>>([])
  const [toastsShown, setToastsShown] = useState(false)

  const dismissToast = useCallback((id: number) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, closing: true } : t))
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, 300)
  }, [])

  // Show insights as toasts when data loads
  useEffect(() => {
    if (!data?.insights || data.insights.length === 0 || toastsShown) return
    setToastsShown(true)

    // Stagger toast appearance
    const maxToasts = 3
    const insightsToShow = data.insights.slice(0, maxToasts)
    insightsToShow.forEach((insight, index) => {
      const isWarning = insight.startsWith('⚠️') || insight.startsWith('🔴')
      const isSuccess = insight.startsWith('✅')
      const type = isWarning ? 'warning' as const : isSuccess ? 'success' as const : 'info' as const
      const cleanText = insight.replace(/^[^\w\s]+\s*/u, '')

      setTimeout(() => {
        setToasts(prev => [...prev, { id: Date.now() + index, text: cleanText, type, closing: false }])
      }, 600 + index * 800)
    })

    // Auto-dismiss non-warning toasts after 8s
    insightsToShow.forEach((insight, index) => {
      const isWarning = insight.startsWith('⚠️') || insight.startsWith('🔴')
      if (!isWarning) {
        setTimeout(() => {
          setToasts(prev => {
            const toast = prev.find(t => !t.closing && t.text === insight.replace(/^[^\w\s]+\s*/u, ''))
            if (toast) {
              return prev.map(t => t.id === toast.id ? { ...t, closing: true } : t)
            }
            return prev
          })
          setTimeout(() => {
            setToasts(prev => prev.filter(t => !t.closing || t.text !== insight.replace(/^[^\w\s]+\s*/u, '')))
          }, 300)
        }, 8000 + index * 800)
      }
    })
  }, [data?.insights, toastsShown])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="card">
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <PiggyBank className="w-12 h-12 text-slate-400 dark:text-slate-500 mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nessun dato disponibile
          </p>
          <p className="text-sm text-slate-400 max-w-[360px] text-center">
            Inizia caricando un estratto conto!
          </p>
        </div>
      </div>
    )
  }

  const { current_month: current, trend, top_categories, insights } = data

  const formatCurrency = (value: number) => {
    if (!showAmounts) return '€ •••••'
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)
  }

  const formatCompact = (value: number) => {
    if (!showAmounts) return '•••'
    if (Math.abs(value) >= 1000) {
      return `€${(value / 1000).toFixed(1)}k`
    }
    return `€${value.toFixed(0)}`
  }

  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']

  // Cash flow chart data
  const cashFlowChartData = cashFlowData?.periods?.map(p => ({
    period: p.period,
    label: (() => {
      const [y, m] = p.period.split('-')
      return `${months[parseInt(m) - 1]} ${y.slice(2)}`
    })(),
    inflows: p.statement.operating.total_inflows + p.statement.investing.total_inflows + p.statement.financing.total_inflows,
    outflows: -(p.statement.operating.total_outflows + p.statement.investing.total_outflows + p.statement.financing.total_outflows),
    net: p.statement.net_cash_flow,
  })) || []

  // Quick date presets
  const setPreset = (preset: 'this_month' | 'last_month' | 'this_quarter' | 'this_year' | 'last_year') => {
    setActivePreset(preset)
    const now = new Date()
    let start: Date, end: Date
    
    switch (preset) {
      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        break
      case 'last_month':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        end = new Date(now.getFullYear(), now.getMonth(), 0)
        break
      case 'this_quarter': {
        const quarter = Math.floor(now.getMonth() / 3)
        start = new Date(now.getFullYear(), quarter * 3, 1)
        end = new Date(now.getFullYear(), quarter * 3 + 3, 0)
        break
      }
      case 'this_year':
        start = new Date(now.getFullYear(), 0, 1)
        end = new Date(now.getFullYear(), 11, 31)
        break
      case 'last_year':
        start = new Date(now.getFullYear() - 1, 0, 1)
        end = new Date(now.getFullYear() - 1, 11, 31)
        break
    }
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    })
  }

  // Tour steps
  const tourSteps: TourStep[] = language === 'it' ? [
    { title: 'Benvenuto in Dashboard! 🎉', content: 'Panoramica finanziaria della tua azienda.', position: 'center' },
    { target: '[data-tour="shortcuts"]', title: 'Scorciatoie', content: 'Accedi rapidamente alle funzioni principali.', position: 'bottom' },
    { target: '[data-tour="fiscal-btn"]', title: 'Scadenze Fiscali', content: 'Clicca sul calendario per le prossime scadenze. Il badge rosso indica le urgenti.', position: 'bottom' },
    { target: '[data-tour="cash-flow"]', title: 'Flusso di Cassa', content: 'Monitora entrate e uscite operative, flusso netto e indicatori di liquidità.', position: 'bottom' },
    { target: '[data-tour="stats-cards"]', title: 'Statistiche', content: 'Entrate, uscite, costi fissi e risparmio del periodo.', position: 'bottom' },
  ] : [
    { title: 'Welcome to Dashboard! 🎉', content: 'Your company\'s financial overview.', position: 'center' },
    { target: '[data-tour="shortcuts"]', title: 'Shortcuts', content: 'Quickly access main features.', position: 'bottom' },
    { target: '[data-tour="fiscal-btn"]', title: 'Fiscal Deadlines', content: 'Click the calendar for upcoming deadlines. Red badge = urgent.', position: 'bottom' },
    { target: '[data-tour="cash-flow"]', title: 'Cash Flow', content: 'Monitor operating inflows/outflows, net cash flow and liquidity.', position: 'bottom' },
    { target: '[data-tour="stats-cards"]', title: 'Quick Stats', content: 'Income, expenses, fixed costs and savings.', position: 'bottom' },
  ]

  return (
    <>
      <TourGuide tourId="dashboard" steps={tourSteps} />

      {/* Refetch indicator */}
      {dashboardFetching && !isLoading && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-3 py-1.5 rounded-lg shadow-md text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {language === 'it' ? 'Aggiornamento...' : 'Updating...'}
        </div>
      )}
      
      {/* Widget Configuration Panel */}
      {isConfiguring && (
        <WidgetConfigPanel
          widgets={widgets}
          onToggle={toggleWidget}
          onMove={moveWidget}
          onReset={resetWidgets}
          onClose={() => setIsConfiguring(false)}
        />
      )}

      {/* Market Ticker */}
      {isWidgetVisible('market-ticker') && (
        <div className="mb-3 -mx-4 lg:-mx-8 -mt-2">
          <MarketTicker marketData={marketData} isLoading={marketLoading} />
        </div>
      )}
      
      <div className="space-y-4 pt-1">
        {/* ── HEADER: Dashboard title + actions ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              Dashboard
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
              {language === 'it' ? 'Analisi della tesoreria' : 'Treasury Analysis'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleShowAmounts}
              className="no-print p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              title={showAmounts ? t.dashboard.hideAmounts : t.dashboard.showAmounts}
            >
              {showAmounts ? <Eye className="w-4 h-4 text-gray-500" /> : <EyeOff className="w-4 h-4 text-gray-500" />}
            </button>
            <button
              data-tour="widget-config"
              onClick={() => setIsConfiguring(true)}
              className="no-print flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
            >
              <Settings2 className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'it' ? 'Widget' : 'Widgets'}</span>
            </button>
          </div>
        </div>

        {/* ── DATE RANGE — moved under title ── */}
        <div data-tour="date-selector" className="card !p-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div className="text-xs text-gray-500 dark:text-gray-400 bg-primary-50 dark:bg-primary-900/30 px-2.5 py-1 rounded-lg inline-flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> {t.dashboard.period}: {new Date(dateRange.start).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US')} – {new Date(dateRange.end).toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US')}
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <button onClick={() => window.print()} className="no-print px-2 py-1 text-xs font-medium rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300 flex items-center gap-1">
                <Printer className="w-3 h-3" /> {t.dashboard.print}
              </button>
              <div className="flex flex-wrap gap-1 no-print">
                {(['this_month', 'last_month', 'this_quarter', 'this_year', 'last_year'] as const).map(preset => (
                  <button key={preset} onClick={() => setPreset(preset)} className={`px-2 py-1 text-xs font-medium rounded-lg transition-colors ${
                    activePreset === preset ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-600 dark:text-gray-300'
                  }`}>
                    {t.dashboard[preset === 'this_month' ? 'thisMonth' : preset === 'last_month' ? 'lastMonth' : preset === 'this_quarter' ? 'quarter' : preset === 'this_year' ? 'year' : 'lastYear']}
                  </button>
                ))}
              </div>
              <div className="no-print flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg px-2 py-1">
                <Calendar className="w-3 h-3 text-gray-400" />
                <input type="date" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))} className="text-xs border-none focus:ring-0 p-0 bg-transparent dark:text-white" />
                <span className="text-gray-400 text-xs">→</span>
                <input type="date" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))} className="text-xs border-none focus:ring-0 p-0 bg-transparent dark:text-white" />
              </div>
            </div>
          </div>
        </div>

        {isWidgetVisible('financial-intelligence') && (
          <FinancialIntelligencePanel
            description="Riconciliazione, previsione di cassa e statement analysis collegati ai dati veri della workspace."
            reconciliation={{
              data: financialIntelligence.reconciliationQuery.data,
              lookup: financialIntelligence.reconciliationQuery.lookup,
              isLoading: financialIntelligence.reconciliationQuery.isLoading,
              isFetching: financialIntelligence.reconciliationQuery.isFetching,
              error: financialIntelligence.reconciliationQuery.error,
              onRetry: () => {
                void financialIntelligence.reconciliationQuery.refetch()
              },
            }}
            cashflow={{
              data: financialIntelligence.cashflowQuery.data,
              isLoading: financialIntelligence.cashflowQuery.isLoading,
              isFetching: financialIntelligence.cashflowQuery.isFetching,
              error: financialIntelligence.cashflowQuery.error,
              onRetry: () => {
                void financialIntelligence.cashflowQuery.refetch()
              },
            }}
            statement={{
              data: financialIntelligence.statementQuery.data,
              isLoading: financialIntelligence.statementQuery.isLoading,
              isFetching: financialIntelligence.statementQuery.isFetching,
              error: financialIntelligence.statementQuery.error,
              onRetry: () => {
                void financialIntelligence.statementQuery.refetch()
              },
            }}
            showAmounts={showAmounts}
          />
        )}

        {/* ── Highlight cards — Entrate/Uscite in ritardo ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="card !p-5">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {language === 'it' ? 'Entrate in ritardo' : 'Late inflows'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {language === 'it' ? `Mese corrente · ${months[new Date().getMonth()]} ${new Date().getFullYear()}` : `Current month · ${months[new Date().getMonth()]} ${new Date().getFullYear()}`}
            </p>
            <p className="text-3xl font-bold text-orange-500 mt-3 tabular-nums">
              {formatCurrency(current.total_income)}
            </p>
          </div>
          <div className="card !p-5">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {language === 'it' ? 'Uscite in ritardo' : 'Late outflows'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {language === 'it' ? `Mese corrente · ${months[new Date().getMonth()]} ${new Date().getFullYear()}` : `Current month · ${months[new Date().getMonth()]} ${new Date().getFullYear()}`}
            </p>
            <p className="text-3xl font-bold text-green-600 mt-3 tabular-nums">
              {formatCurrency(current.total_expenses)}
            </p>
          </div>
          
          {/* Entrate realizzate vs previsioni */}
          {isWidgetVisible('trend-chart') && timeline && timeline.length > 0 && (
            <div className="card !p-4">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                {language === 'it' ? 'Entrate - Realizzate vs. previsioni' : 'Income - Actual vs. forecast'}
              </p>
              <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-0.5">
                {language === 'it' ? `Ultimi 6 mesi` : `Last 6 months`}
              </p>
              <ResponsiveContainer width="100%" height={120}>
                <ComposedChart data={timeline.slice(-6)} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" tickFormatter={(v) => { const [, month] = v.split('-'); return months[parseInt(month) - 1] }} tick={{ fill: axisColor, fontSize: 9 }} stroke={axisColor} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} contentStyle={{ fontSize: '10px', borderRadius: '6px' }} />
                  <Bar dataKey="income" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={20} name={language === 'it' ? 'Transazioni' : 'Transactions'} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

      {/* ── KPI CARDS ── */}
      {isWidgetVisible('kpi-cards') && (
      <div data-tour="stats-cards" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="card !p-4 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="kpi-label">{t.dashboard.income}</p>
              <p className="kpi-value text-green-600">{formatCurrency(current.total_income)}</p>
            </div>
            <div className="p-2 bg-green-50 dark:bg-green-900/30 rounded-lg"><ArrowUpRight className="w-4 h-4 text-green-600" /></div>
          </div>
        </div>
        <div className="card !p-4 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="kpi-label">{t.dashboard.expenses}</p>
              <p className="kpi-value text-red-600">{formatCurrency(current.total_expenses)}</p>
              {trend.expenses !== 0 && (
                <p className={`kpi-trend ${trend.expenses > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {trend.expenses > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {Math.abs(trend.expenses).toFixed(1)}% {t.dashboard.vsLastMonth}
                </p>
              )}
            </div>
            <div className="p-2 bg-red-50 dark:bg-red-900/30 rounded-lg"><ArrowDownRight className="w-4 h-4 text-red-600" /></div>
          </div>
        </div>
        <div className="card !p-4 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="kpi-label">{t.dashboard.fixedCosts}</p>
              <p className="kpi-value text-blue-600">{formatCurrency(current.fixed_costs)}</p>
              <p className="kpi-trend text-gray-400 dark:text-gray-500">{current.fixed_percentage.toFixed(1)}% {t.dashboard.ofExpenses}</p>
            </div>
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg"><DollarSign className="w-4 h-4 text-blue-600" /></div>
          </div>
        </div>
        <div className="card !p-4 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="kpi-label">{t.dashboard.savings}</p>
              <p className={`kpi-value ${current.savings >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(current.savings)}</p>
            </div>
            <div className={`p-2 rounded-lg ${current.savings >= 0 ? 'bg-green-50 dark:bg-green-900/30' : 'bg-red-50 dark:bg-red-900/30'}`}>
              <PiggyBank className={`w-4 h-4 ${current.savings >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </div>
        </div>
      </div>
      )}

      {/* ── CASH FLOW — Main widget ── */}
      {isWidgetVisible('cash-flow') && (
      <div data-tour="cash-flow" className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            {language === 'it' ? 'Flusso di Cassa' : 'Cash Flow'}
          </h3>
        </div>

        {cashFlowLoading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>
        ) : cashFlowData ? (
          <div className="space-y-4">
            {/* Cash Flow KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <ArrowUp className="w-3 h-3 text-green-600" />
                  <span className="text-[10px] font-medium text-green-700 dark:text-green-400">
                    {language === 'it' ? 'Entrate Operative' : 'Operating Inflows'}
                  </span>
                </div>
                <p className="text-base font-bold text-green-600 tabular-nums">{formatCurrency(cashFlowData.summary.operating.total_inflows)}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <ArrowDown className="w-3 h-3 text-red-600" />
                  <span className="text-[10px] font-medium text-red-700 dark:text-red-400">
                    {language === 'it' ? 'Uscite Operative' : 'Operating Outflows'}
                  </span>
                </div>
                <p className="text-base font-bold text-red-600 tabular-nums">{formatCurrency(Math.abs(cashFlowData.summary.operating.total_outflows))}</p>
              </div>
              <div className={`rounded-lg p-3 ${cashFlowData.summary.net_cash_flow >= 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-orange-50 dark:bg-orange-900/20'}`}>
                <div className="flex items-center gap-1 mb-1">
                  <Wallet className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">
                    {language === 'it' ? 'Flusso Netto' : 'Net Cash Flow'}
                  </span>
                </div>
                <p className={`text-base font-bold tabular-nums ${cashFlowData.summary.net_cash_flow >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                  {formatCurrency(cashFlowData.summary.net_cash_flow)}
                </p>
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="flex items-center gap-1 mb-1">
                  <Activity className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                  <span className="text-[10px] font-medium text-gray-700 dark:text-gray-300">Burn Rate</span>
                </div>
                <p className="text-base font-bold text-gray-700 dark:text-gray-200 tabular-nums">
                  {cashFlowData.kpis.cash_burn_rate > 0 ? formatCurrency(cashFlowData.kpis.cash_burn_rate) : '—'}
                  {cashFlowData.kpis.runway_months > 0 && cashFlowData.kpis.runway_months < 999 && (
                    <span className="text-[10px] font-normal text-gray-500 ml-1">({cashFlowData.kpis.runway_months.toFixed(0)} {language === 'it' ? 'mesi' : 'mo'})</span>
                  )}
                </p>
              </div>
            </div>

            {/* Cash Flow Chart — Agicap-style vertical bars */}
            {cashFlowChartData.length > 0 && (
              <div>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={cashFlowChartData} margin={{ top: 8, right: 10, left: 0, bottom: 0 }} barGap={2} barCategoryGap="20%">
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={isDark ? '#334155' : '#f1f5f9'} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: axisColor }} stroke={axisColor} tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={(v) => formatCompact(v)} tick={{ fontSize: 10, fill: axisColor }} stroke={axisColor} tickLine={false} axisLine={false} />
                    <ReferenceLine y={0} stroke={isDark ? '#475569' : '#cbd5e1'} strokeWidth={1} />
                    <Tooltip
                      cursor={{ fill: isDark ? 'rgba(148,163,184,0.08)' : 'rgba(148,163,184,0.12)', radius: 4 }}
                      formatter={(value: number, name: string) => [
                        formatCurrency(Math.abs(value)),
                        name === 'inflows' ? (language === 'it' ? 'Entrate' : 'Inflows')
                          : name === 'outflows' ? (language === 'it' ? 'Uscite' : 'Outflows')
                          : (language === 'it' ? 'Flusso Netto' : 'Net')
                      ]}
                      contentStyle={{ background: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, borderRadius: '8px', fontSize: '11px' }}
                    />
                    <Bar dataKey="inflows" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="outflows" fill="#ef4444" radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Line type="monotone" dataKey="net" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: '#3b82f6', strokeWidth: 0 }} activeDot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-[10px] text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-[2px]" /> {language === 'it' ? 'Entrate' : 'Inflows'}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-500 rounded-[2px]" /> {language === 'it' ? 'Uscite' : 'Outflows'}</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-blue-500 rounded" /> {language === 'it' ? 'Netto' : 'Net'}</span>
                </div>
              </div>
            )}

            {/* Cash Flow Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="bg-green-50/50 dark:bg-green-900/10 rounded-lg p-3">
                <h4 className="text-[10px] font-semibold text-green-700 dark:text-green-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <ArrowUp className="w-2.5 h-2.5" /> {language === 'it' ? 'Top Entrate Operative' : 'Top Operating Inflows'}
                </h4>
                <div className="space-y-1">
                  {cashFlowData.summary.operating.inflows.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-gray-700 dark:text-gray-300 truncate flex-1">{item.category_name}</span>
                      <span className="text-green-600 dark:text-green-400 font-medium tabular-nums ml-2">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                  {cashFlowData.summary.operating.inflows.length === 0 && <p className="text-[10px] text-gray-400">—</p>}
                </div>
              </div>
              <div className="bg-red-50/50 dark:bg-red-900/10 rounded-lg p-3">
                <h4 className="text-[10px] font-semibold text-red-700 dark:text-red-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <ArrowDown className="w-2.5 h-2.5" /> {language === 'it' ? 'Top Uscite Operative' : 'Top Operating Outflows'}
                </h4>
                <div className="space-y-1">
                  {cashFlowData.summary.operating.outflows.slice(0, 5).map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-gray-700 dark:text-gray-300 truncate flex-1">{item.category_name}</span>
                      <span className="text-red-600 dark:text-red-400 font-medium tabular-nums ml-2">{formatCurrency(Math.abs(item.amount))}</span>
                    </div>
                  ))}
                  {cashFlowData.summary.operating.outflows.length === 0 && <p className="text-[10px] text-gray-400">—</p>}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8 text-sm">{t.dashboard.noData}</p>
        )}
      </div>
      )}

      {/* ── CHARTS ROW ── */}
      {(isWidgetVisible('expense-chart') || isWidgetVisible('fixed-variable-chart')) && (
      <div data-tour="expense-chart" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {isWidgetVisible('expense-chart') && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">{t.dashboard.expenseDistribution}</h3></div>
          {top_categories.length > 0 ? (
            <div className="flex items-center">
              <div className="w-1/2">
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie data={top_categories.slice(0, 5)} dataKey="total" nameKey="category_name" cx="50%" cy="50%" outerRadius={65} label={false}>
                      {top_categories.slice(0, 5).map((cat, index) => (<Cell key={index} fill={cat.color} />))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-1.5">
                {top_categories.slice(0, 5).map((cat) => (
                  <div key={cat.category_id} className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    <CategoryIcon icon={cat.icon} className="w-3 h-3 flex-shrink-0" style={{ color: cat.color }} />
                    <span className="text-xs text-gray-600 dark:text-gray-300 flex-1 truncate">{cat.category_name}</span>
                    <span className="text-xs font-medium dark:text-white tabular-nums">{cat.percentage.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-6 text-xs">{t.dashboard.noData}</p>
          )}
        </div>
        )}
        {isWidgetVisible('fixed-variable-chart') && (
        <div data-tour="top-categories" className="card">
          <div className="card-header"><h3 className="card-title">{t.dashboard.fixedVsVariable}</h3></div>
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={[
              { name: t.dashboard.fixed, value: current.fixed_costs, fill: '#3b82f6' },
              { name: t.dashboard.variable, value: current.variable_costs, fill: '#f59e0b' }
            ]} layout="vertical">
              <XAxis type="number" tickFormatter={(v) => `€${v}`} tick={{ fill: axisColor, fontSize: 10 }} stroke={axisColor} />
              <YAxis dataKey="name" type="category" width={50} tick={{ fill: axisColor, fontSize: 10 }} stroke={axisColor} />
              <Tooltip formatter={(value: number) => formatCurrency(value)} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex justify-center gap-5 text-xs">
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-blue-500 rounded-sm" /><span className="text-gray-600 dark:text-gray-400">{t.dashboard.fixed}: {current.fixed_percentage.toFixed(1)}%</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-amber-500 rounded-sm" /><span className="text-gray-600 dark:text-gray-400">{t.dashboard.variable}: {current.variable_percentage.toFixed(1)}%</span></div>
          </div>
        </div>
        )}
      </div>
      )}

      {/* ── TIMELINE ── */}
      {isWidgetVisible('trend-chart') && timeline && timeline.length > 0 && (
        <div className="card">
          <div className="card-header"><h3 className="card-title">{t.dashboard.timeline}</h3></div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={timeline}>
              <XAxis dataKey="month" tickFormatter={(v) => { const [, month] = v.split('-'); return months[parseInt(month) - 1] }} tick={{ fill: axisColor, fontSize: 10 }} stroke={axisColor} />
              <YAxis tickFormatter={(v) => `€${v / 1000}k`} tick={{ fill: axisColor, fontSize: 10 }} stroke={axisColor} />
              <Tooltip formatter={(value: number) => formatCurrency(value)} labelFormatter={(label) => { const [year, month] = label.split('-'); return `${months[parseInt(month) - 1]} ${year}` }} />
              <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2} name={t.dashboard.income} />
              <Line type="monotone" dataKey="expenses" stroke="#ef4444" strokeWidth={2} name={t.dashboard.expenses} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── INSIGHTS — inline ── */}
      {isWidgetVisible('insights') && insights && insights.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center gap-2"><Lightbulb className="w-4 h-4 text-yellow-500" /> {t.dashboard.insights}</h3>
          </div>
          <div className="space-y-2">
            {insights.map((insight, index) => {
              const isWarning = insight.startsWith('⚠️') || insight.startsWith('🔴')
              const isSuccess = insight.startsWith('✅')
              const cleanText = insight.replace(/^[^\w\s]+\s*/u, '')
              return (
                <div key={index} className={`p-3 rounded-lg flex items-start gap-2 ${
                  isWarning ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800'
                    : isSuccess ? 'bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800'
                }`}>
                  {isWarning && <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />}
                  {isSuccess && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />}
                  {!isWarning && !isSuccess && <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />}
                  <p className="text-sm text-gray-700 dark:text-gray-300">{cleanText}</p>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>

      {/* ── TOAST NOTIFICATIONS — bottom right ── */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`flex items-start gap-2.5 p-3.5 rounded-xl shadow-elevated border backdrop-blur-sm ${
                toast.closing ? 'animate-toast-out' : 'animate-toast-in'
              } ${
                toast.type === 'warning'
                  ? 'bg-amber-50/95 dark:bg-amber-900/90 border-amber-200 dark:border-amber-700'
                  : toast.type === 'success'
                  ? 'bg-green-50/95 dark:bg-green-900/90 border-green-200 dark:border-green-700'
                  : 'bg-white/95 dark:bg-slate-800/90 border-blue-200 dark:border-blue-700'
              }`}
            >
              {toast.type === 'warning' && <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />}
              {toast.type === 'success' && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />}
              {toast.type === 'info' && <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />}
              <p className="text-xs text-gray-700 dark:text-gray-300 flex-1 leading-relaxed">{toast.text}</p>
              <button
                onClick={() => dismissToast(toast.id)}
                className="p-0.5 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors flex-shrink-0"
              >
                <X className="w-3.5 h-3.5 text-gray-400" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  )
}
